import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import * as _ from 'lodash-es';
import { Subject } from 'rxjs';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { MessagingService } from 'src/app/core/messaging/messaging.service';
import { TranslationService } from 'src/app/core/translation/translation.service';
import { MapPanAndZoomStream } from 'src/app/feature/map-viewer/common/map-pan-zoom-stream.service';
import { MapSelectionStreamService } from 'src/app/feature/map-viewer/map-selection/selection-stream.service';
import { MapService } from 'src/app/feature/map-viewer/map.service';
import { ContextMenuActions } from 'src/app/shared/common/components/context-menu/gsp-context-menu.component';
import { GeometryTypeSelectionCriteria } from 'src/app/shared/common/current-features/selection-criteria';
import { LayersStore } from 'src/app/shared/common/current-layers/layers-store.service';
import { LayersStreams } from 'src/app/shared/common/current-layers/layers-streams.service';
import { MapWorkspacesStoreService } from 'src/app/shared/common/current-map-workspaces/map-workspaces-store.service';
import { ProjectStreamService } from 'src/app/shared/common/current-project/project-stream.service';
import { TemplatesStoreService } from 'src/app/shared/common/current-templates/templates-store.service';
import { CurrentUserStreamService } from 'src/app/shared/common/current-user/current-user-stream.service';
import { LegacyTasksStore } from 'src/app/shared/common/legacy-current-tasks/legacy-tasks-store.service';
import { Layer } from 'src/app/shared/map-data-services/layer/layer';
import { TemplateTypeId } from 'src/app/shared/map-data-services/layer/template-lite';
import { MapWorkspace } from 'src/app/shared/map-data-services/mapWorkspace/map-workspace';
import { MapWorkspacePermissionType } from 'src/app/shared/map-data-services/mapWorkspace/map-workspace-permission';
import { WorkspaceType } from 'src/app/shared/map-data-services/mapWorkspace/map-workspace.types';

import { transition, trigger, useAnimation } from '@angular/animations';
import { dropdownCloseAnimation, dropdownOpenAnimation } from 'src/app/shared/common/animations/gsp-animations';
import { FeatureFilterStreamService } from 'src/app/shared/common/current-features/feature-filter-stream.service';
import { GeometryTypes } from 'src/app/shared/common/utility/geometry-utils';
import { CachedFeature, CachedFeatureService } from 'src/app/shared/map-data-services/feature/cached-feature.service';
import { LayerStyleGenerator } from 'src/app/shared/map-data-services/layer/layer-style-generator';
import { ProjectTaskMigrationStatus } from 'src/app/shared/map-data-services/project/project';
import { SymbologyOverrideChoice } from 'src/app/shared/map-data-services/styles/style-symbology';
import { CodedChoiceField, CodedChoiceValue, FieldType, GroupField, LayoutFieldType } from 'src/app/shared/template-services/field';
import { LayerListService } from './layer-list.service';
import { RemoveLayerComponent } from './remove-layer.component';

@Component({
    templateUrl: './layer-list.component.html',
    selector: 'layer-list',
    animations: [
        trigger('dropDown', [
            transition(':enter', [useAnimation(dropdownOpenAnimation)]),
            transition(':leave', [useAnimation(dropdownCloseAnimation)])
        ])
    ]
})
export class LayerListComponent implements OnInit, OnDestroy {
    // Make WorkspaceType available in the template
    public WorkspaceType = WorkspaceType;

    public contextMenuItems: ContextMenuActions[] = [
        {
            name: this.translate.instant('TC.Common.ZoomToLayer'),
            id: 'zoom-to-layer',
            labelClass: 'layer-context-menu-item',
            title: false,
            visible: ($event: Layer) =>
                this.layerlistService.canViewLayer($event) && this.layerlistService.canZoomToExtent($event),
            execute: ($event: { item: Layer }) => this.zoomToLayerExtents($event.item)
        },
        {
            name: this.translate.instant('TCS.Mapviewer.LayerContextMenu.SelectFormNoLocation'),
            id: 'select-form-no-location',
            labelClass: 'layer-context-menu-item',
            title: false,
            visible: ($event: Layer) => this.layerlistService.canViewLayer($event) && $event.nonSpatialCount > 0,
            execute: ($event: { item: Layer }) => this.addNonSpatialToPanel($event.item)
        },
        {
            name: null,
            execute: () => null,
            divider: true,
            title: false,
            visible: () => true
        },
        {
            name: this.translate.instant('TCS.Mapviewer.LayerContextMenu.EditLayer'),
            id: 'edit-layer',
            labelClass: 'layer-context-menu-item',
            title: false,
            visible: ($event: Layer) =>
                !this.layerlistService.restrictAccess([MapWorkspacePermissionType.FULL_ACCESS]) && this.layerlistService.visibleOnlyFor([WorkspaceType.GIS]) &&
                this.layerlistService.canEditLayer($event) &&
                !this.isLayerSublayer($event),
            execute: ($event: { item: Layer }) => this.goToEditModal($event.item)
        },
        {
            name: this.translate.instant('TCS.Mapviewer.LayerContextMenu.LockLayer'),
            id: 'lock-layer',
            labelClass: 'layer-context-menu-item',
            title: false,
            visible: ($event: Layer) =>
                !this.layerlistService.restrictAccess([MapWorkspacePermissionType.FULL_ACCESS]) && this.layerlistService.visibleOnlyFor([WorkspaceType.GIS]) &&
                this.layerlistService.canLockLayer($event) &&
                $event.templateId &&
                !$event.isLocked &&
                !this.isLayerSublayer($event),
            execute: ($event: { item: Layer }) => this.changeLockStatus($event.item, true)
        },
        {
            name: this.translate.instant('TCS.Mapviewer.LayerContextMenu.UnlockLayer'),
            id: 'unlock-layer',
            labelClass: 'layer-context-menu-item',
            title: false,
            visible: ($event: Layer) =>
                !this.layerlistService.restrictAccess([MapWorkspacePermissionType.FULL_ACCESS]) && this.layerlistService.visibleOnlyFor([WorkspaceType.GIS]) &&
                this.layerlistService.canLockLayer($event) &&
                $event.templateId &&
                $event.isLocked &&
                !this.isLayerSublayer($event),
            execute: ($event: { item: Layer }) => this.changeLockStatus($event.item, false)
        },
        {
            name: this.translate.instant('TCS.Mapviewer.LayerContextMenu.RemoveLayer'),
            id: 'remove-layer',
            labelClass: 'layer-context-menu-item',
            title: false,
            visible: ($event: Layer) =>
                !this.layerlistService.restrictAccess([MapWorkspacePermissionType.FULL_ACCESS]) && this.layerlistService.visibleOnlyFor([WorkspaceType.GIS]) &&
                !this.isLayerSublayer($event),
            execute: ($event: { item: Layer }) => this.removeLayer($event.item)
        },
        {
            name: null,
            execute: () => null,
            divider: true,
            title: false,
            visible: () => true
        },
        {
            name: this.translate.instant('TC.Common.CreateTemplate'),
            id: 'create-template',
            labelClass: 'layer-context-menu-item',
            title: false,
            visible: ($event: Layer) =>
                !this.layerlistService.restrictAccess([MapWorkspacePermissionType.FULL_ACCESS]) && this.layerlistService.visibleOnlyFor([WorkspaceType.GIS]) &&
                this.layerlistService.canCreateTemplateFromLayer($event) &&
                !this.isLayerSublayer($event),
            execute: ($event: { item: Layer }) => this.layerToTemplate($event.item)
        },
        {
            name: this.translate.instant('TC.Common.DuplicateTemplate'),
            id: 'duplicate-template',
            labelClass: 'layer-context-menu-item',
            title: false,
            visible: ($event: Layer) =>
                !this.layerlistService.restrictAccess([MapWorkspacePermissionType.FULL_ACCESS]) && this.layerlistService.visibleOnlyFor([WorkspaceType.GIS]) &&
                this.layerlistService.canEditTemplate($event) &&
                !this.isLayerSublayer($event),
            execute: ($event: { item: Layer }) => this.layersStore.createDuplicateLayer($event.item)
        },
        {
            name: this.translate.instant('TC.Common.EditTemplate'),
            id: 'edit-template',
            labelClass: 'layer-context-menu-item',
            title: false,
            visible: ($event: Layer) =>
                !this.layerlistService.restrictAccess([MapWorkspacePermissionType.FULL_ACCESS]) && this.layerlistService.visibleOnlyFor([WorkspaceType.GIS]) &&
                this.layerlistService.canEditTemplate($event) &&
                !this.isLayerSublayer($event),
            execute: ($event: { item: Layer }) => this.goToTemplateEditor($event.item)
        },
        {
            name: this.translate.instant('TC.Common.SelectAllForms'),
            id: 'select-all-forms',
            labelClass: 'layer-context-menu-item',
            title: false,
            visible: ($event: Layer) =>
                this.layerlistService.canViewLayer($event) && (!!$event.bounds || $event.nonSpatialCount > 0),
            execute: ($event: { item: Layer }) => this.selectAllForms($event.item)
        },
        {
            name: this.translate.instant('MapViewer.Template.DiscardDraft.Menu'),
            id: 'discard-draft-changes',
            labelClass: 'layer-context-menu-item',
            title: false,
            visible: ($event: Layer) =>
                this.layerlistService.canDiscardDraftChanges($event) && this.layerlistService.visibleOnlyFor([WorkspaceType.GIS]) && !this.isLayerSublayer($event),
            execute: ($event: { item: Layer }) =>
                this.goToCenterDialog('layer/discardDraftChanges', { layerId: $event.item.id }, true)
        }
    ];

    public loading = true;
    public currentMapWorkspace: MapWorkspace;
    public currentUserIsAdmin: boolean;
    public layers: Layer[];
    public visibleAll: boolean;
    public loadingLayerIds: string[];
    public templateLayers: Layer[];
    public layerSymbologyOverridesMap: Map<string, SymbologyOverrideChoice[]> = new Map<
        string,
        SymbologyOverrideChoice[]
    >();
    public layerSublayersDropVisibleMap: Map<string, boolean> = new Map<string, boolean>();
    public sublayersVisibleMap: Map<string, boolean> = new Map<string, boolean>();
    public tooltipClosed = false;

    // exposing enum to template
    public MapWorkspacePermissionType = MapWorkspacePermissionType;
    public GeometryTypes = GeometryTypes;

    private destroyed = new Subject<void>();

    @ViewChild(RemoveLayerComponent)
    removeLayerComponent: RemoveLayerComponent;

    constructor(
        private router: Router,
        private layersStreams: LayersStreams,
        private layersStore: LayersStore,
        private mapWorkspacesStore: MapWorkspacesStoreService,
        private messaging: MessagingService,
        private mapPanAndZoomStream: MapPanAndZoomStream,
        private tasksStore: LegacyTasksStore,
        private selectionStream: MapSelectionStreamService,
        private mapService: MapService,
        private projectStream: ProjectStreamService,
        private currentUserStream: CurrentUserStreamService,
        private translate: TranslationService,
        private layerlistService: LayerListService,
        private featureFilterStream: FeatureFilterStreamService,
        private cachedFeatureService: CachedFeatureService,
        private templatesStore: TemplatesStoreService
    ) {}

    ngOnInit(): void {
        this.projectStream
            .getUserWithRolesForCurrentProject(this.currentUserStream.currentUser.id)
            .then(userWithRole => {
                this.currentUserIsAdmin = userWithRole.role === 'ADMIN' ? true : false;
            });

        this.layers = [];

        this.currentMapWorkspace = this.mapWorkspacesStore.getCurrentMapWorkspace();

        this.layersStore.mapWorkspaceLayersLoadingStream.pipe(takeUntil(this.destroyed)).subscribe(loading => {
            this.loading = loading;
        });

        this.layersStore.mapWorkspaceLayersStream.pipe(takeUntil(this.destroyed)).subscribe(tmpLayers => {
            if (tmpLayers) {
                this.currentMapWorkspace = this.mapWorkspacesStore.getCurrentMapWorkspace();
                tmpLayers = _.uniqBy(tmpLayers, 'id');

                // updating only the new layers on layer import
                _.differenceWith(tmpLayers, this.layers, _.isEqual).forEach(layer => {
                    if (layer.visible) {
                        this.layersStreams.deferVisibleLayersStream.next([layer]);
                    }
                });

                this.layers = this.layersStreams.sortLayers(tmpLayers);
                this.createLayerSymbologyOverridesMap();

                let visibleLayers = this.layers.filter(layer => layer.visible);
                if (visibleLayers.length < this.layers.length) {
                    this.visibleAll = false;
                } else if (visibleLayers.length === this.layers.length) {
                    this.visibleAll = true;
                }
                this.layersStreams.setVisibleLayers(visibleLayers, false);
            }
        });

        this.loadingLayerIds = [];
        this.layersStreams.loadingLayersStream.pipe(takeUntil(this.destroyed)).subscribe(layers => {
            this.loadingLayerIds = layers.map(layer => layer.id);
        });

        this.layersStore.changedMapWorkspaceLayerStream
            .pipe(takeUntil(this.destroyed), distinctUntilChanged())
            .subscribe((layer: Layer) => {
                if (layer && layer.id && layer.isUpdating === true) {
                    let layerIndex = this.layers.findIndex(layer2 => layer2.id === layer.id);
                    if (layerIndex !== -1) {
                        this.layers[layerIndex].isUpdating = layer.isUpdating;
                    } else if (layer.workspaceId === this.currentMapWorkspace.id) {
                        this.layers = this.layers || [];
                        this.layers.unshift(layer);
                    }
                }
            });
    }

    ngOnDestroy(): void {
        this.destroyed.next(null);
    }

    public layersAreLoading(): boolean {
        return this.loadingLayerIds.length > 0;
    }

    public layerIsLoading(layer: Layer): boolean {
        return layer && (this.loadingLayerIds.indexOf(layer.id) >= 0 || !layer.allDerivedPropertiesLoaded);
    }

    private toggleLayerAndAllVisibility(layer: Layer): void {
        layer.visible = !layer.visible;
        let visibleLayers = this.layers.filter(layr => layr.visible);
        if (visibleLayers.length < this.layers.length) {
            this.visibleAll = false;
        } else if (visibleLayers.length === this.layers.length) {
            this.visibleAll = true;
        }

        this.layersStreams.setVisibleLayers(
            visibleLayers,
            this.currentMapWorkspace.isPubliclySharedMapWorkspace || this.currentMapWorkspace.isFileViewer
                ? false
                : true
        );
    }

    public toggle(index: number, canUpdateSubLayer: boolean = true): void {
        const layer = this.layers[index];
        this.toggleLayerAndAllVisibility(layer);

        if (canUpdateSubLayer) {
            // Clear any sublayer visibility setting.
            const hiddenSublayerFeaturesMap = this.layersStreams.hideSublayerFeatureIdsStream.getValue();
            const hiddenLayer = hiddenSublayerFeaturesMap.find(hiddenLayer => hiddenLayer.hasOwnProperty(layer.id));
            if (hiddenLayer) {
                hiddenLayer[layer.id] = [];
            }

            if (this.layers[index].visible) {
                this.resetSublayerVisibilityForLayer(layer, true);
                this.layersStreams.deferVisibleLayersStream.next([this.layers[index]]);
            } else {
                this.resetSublayerVisibilityForLayer(layer, false);
                this.layersStreams.deferHideLayersStream.next([this.layers[index]]);
            }
        }
    }

    public toggleAll(): void {
        this.visibleAll = !this.visibleAll;
        const visibleLayers: Layer[] = [];
        this.layers.forEach(layer => {
            layer.visible = this.visibleAll;
            // Set all sublayer visibilities for this layer
            this.resetSublayerVisibilityForLayer(layer, this.visibleAll);
            if (this.visibleAll) visibleLayers.push(layer);
        });

        // Clear all hidden sublayer settings
        this.layersStreams.hideSublayerFeatureIdsStream.next([]);

        this.layersStreams.setVisibleLayers(
            visibleLayers,
            !(this.currentMapWorkspace.isPubliclySharedMapWorkspace || this.currentMapWorkspace.isFileViewer)
        );

        if (this.visibleAll) {
            this.layersStreams.deferVisibleLayersStream.next(this.layers);
        } else {
            this.layersStreams.deferHideLayersStream.next(this.layers);
        }
    }

    public isTemplateLayerExist(): boolean {
        this.templateLayers = this.layers.filter(layer => layer.geoLayerType === 'TemplateLayer');
        return this.templateLayers.length > 0;
    }

    public async hasActiveTasks(layer: Layer): Promise<boolean> {
        let activeTaskByTemplateSeriesId = this.tasksStore.mapWorkspaceActiveTasksByTemplateSeriesIdStream.getValue();
        if (activeTaskByTemplateSeriesId === null) {
            await this.tasksStore.refreshMapWorkspaceTasks();
            activeTaskByTemplateSeriesId = this.tasksStore.mapWorkspaceActiveTasksByTemplateSeriesIdStream.getValue();
        }

        if (
            activeTaskByTemplateSeriesId &&
            activeTaskByTemplateSeriesId[layer.templateSeriesId] &&
            _.some(activeTaskByTemplateSeriesId[layer.templateSeriesId], task => !task.isClosed)
        ) {
            const migrationStatus = this.projectStream.taskMigrationStatusStream.getValue();
            migrationStatus === ProjectTaskMigrationStatus.COMPLETED
                ? this.messaging.showWarning(this.translate.instant('TCS.Mapviewer.LayerPanel.TemplateHasActiveTask'))
                : this.messaging.showWarning(this.translate.instant('TCS.Mapviewer.LayerPanel.TemplateHasActiveTodo'));
            return true;
        }
        return false;
    }

    showCreateTemplateAction(layer: Layer): boolean {
        return this.layerlistService.canCreateTemplateFromLayer(layer);
    }

    // This used for deleting imported layers in error - see removeLayerComponent for remove of other layers
    public deleteImportedErrorLayer(layer: Layer): void {
        this.layersStore.deleteLayer(layer).catch(() => {
            this.messaging.showError(this.translate.instant('TCS.Import.ErrorDeletingLayer'));
        });
    }

    public async changeLockStatus(layer: Layer, lockStatus: boolean): Promise<void> {
        if (layer.isLocked !== lockStatus) {
            if ((lockStatus && !(await this.hasActiveTasks(layer))) || !lockStatus) {
                layer.isLocked = lockStatus;
                this.layersStore.lockOrUnlockLayer(layer, lockStatus).then(() => {
                    if (lockStatus) {
                        this.messaging.showSuccess(
                            this.translate.instant('TCS.Mapviewer.LayerPanel.LayerLockSuccessfully')
                        );
                    } else {
                        this.messaging.showSuccess(
                            this.translate.instant('TCS.Mapviewer.LayerPanel.LayerUnlockSuccessfully')
                        );
                    }
                    // Remove the cached layers by mapworkspace Id in case of lock status change of linked layers
                    this.layersStore.removeLayersIndexByMapWorkspace(layer.workspaceId);
                });
            }
        }
    }

    public zoomToLayerExtents(layer: Layer): void {
        if (this.layerlistService.canZoomToExtent(layer) || this.isLayerSublayer(layer)) {
            this.layersStore.loadLayersBounds([layer]).then(bounds => {
                let basemapMode = this.mapService.selectedBaseMapMode;
                this.mapPanAndZoomStream.zoomToBounds(bounds, [360, 60], [340, 10], {
                    maxZoom: basemapMode.maxZoom
                });
                this.layersStreams.deferVisibleLayersStream.next([layer]);
            });
        }
    }

    public layerToTemplate(layer: Layer): void {
        if (layer.fileVersionId) {
            this.router.navigate(['template/wizard'], {
                queryParams: {
                    templateId: TemplateTypeId.FROM_LAYER,
                    layerId: layer.id
                },
                queryParamsHandling: 'merge'
            });
        } else {
            this.router.navigate(['template/editor'], {
                queryParams: { [TemplateTypeId.FROM_LAYER]: layer.id },
                queryParamsHandling: 'merge'
            });
        }
    }

    public addNonSpatialToPanel(layer: Layer): void {
        this.selectionStream.stream.next(new GeometryTypeSelectionCriteria([layer], 'none'));
    }

    public goToEditModal(layer: Layer): void {

        if (this.currentMapWorkspace.isPubliclySharedMapWorkspace || this.currentMapWorkspace.isFileViewer) return;

        this.layersStreams.currentEditLayerStream.next(layer);
        this.goToCenterDialog('layer/edit', {}, true);
    }

    public isDisabled(layer: Layer): boolean {
        return (layer && this.layerIsLoading(layer)) || layer.isUpdating;
    }

    public goToTemplateEditor(layer: Layer): void {
        this.router.navigate(['template/editor'], {
            queryParams: { layerId: layer.id, templateId: layer.templateId },
            queryParamsHandling: 'merge'
        });
    }

    public async removeLayer(layer: Layer): Promise<void> {
        if (!(await this.hasActiveTasks(layer))) {
            try {
                await this.layersStore.removeLayerFromMapWorkspace(layer);
                this.layersStreams.deferHideLayersStream.next([layer]);
                const filter = this.featureFilterStream.activeFilter;
                if (filter?.selectedLayer === layer.id) {
                    filter.selectedLayer = null;
                    this.featureFilterStream.activeFilter = filter;
                }
                this.showSuccess(layer);
            } catch {
                this.messaging.showError(this.translate.instant('TC.Common.ErrorWhileDeleting'));
            }
        }
    }

    private showSuccess(deletedlayer: Layer): void {
        let wid = this.currentMapWorkspace.id;
        let pid = this.currentMapWorkspace.projectId;

        let toastr = this.messaging.showSuccess(
            this.translate.instant('TCS.Mapviewer.LayerPanel.LayerRemoved') +
                '<br>' +
                this.translate.instant('RESTORE') +
                ' ' +
                deletedlayer.layerName,
            null,
            { enableHtml: true }
        );

        toastr.onTap.subscribe(() => {
            // restore layer
            this.restoreLayer(pid, wid, deletedlayer);
        });
    }

    private async restoreLayer(currentProjectId: string, currentWorkspaceId: string, layer: Layer): Promise<Layer> {
        try {
            await this.layersStore.addLayerToMapWorkspace(currentProjectId, currentWorkspaceId, layer);
            this.messaging.showSuccess(this.translate.instant('TCS.Mapviewer.LayerPanel.RestoreSuccess'));
            return layer;
        } catch (e) {
            this.messaging.showError(this.translate.instant('TC.Common.ErrorWhileRestoring'));
            return await Promise.resolve(null);
        }
    }

    private selectAllForms(layer: Layer): void {
        this.selectionStream.stream.next(new GeometryTypeSelectionCriteria([layer], null));
    }

    private goToCenterDialog(
        centerDialog: string,
        queryParams?: { [key: string]: string },
        skipLocationChange = false
    ): void {
        this.router.navigate(['mapViewer', { outlets: { centerDialog } }], {
            queryParams,
            queryParamsHandling: 'merge',
            skipLocationChange
        });
    }

    private createLayerSymbologyOverridesMap(): void {
        this.layers.forEach(layer => {
            if (layer.style.styleOverrides?.length) {
                const styleOverridesWithId = this.syncStyleOverrideIds(layer);

                // Load template data to get choice descriptions
                if (layer.templateId) {
                    this.templatesStore.getTemplateById(layer.templateId).then(template => {
                        if (template?.fields) {
                            // Flatten fields if they're in groups
                            const allFields = template.fields.flatMap(field =>
                                field.type === LayoutFieldType.Group ? (field as GroupField).fields : field
                            );

                            // Update style overrides with choice descriptions
                            styleOverridesWithId.forEach(override => {
                                // Set default display value to choice value
                                (override as SymbologyOverrideChoice).choiceDisplayValue = override.choiceValue;

                                const codedChoiceField = allFields.find(field =>
                                    field.name === override.choiceField && field.type === FieldType.CodedChoice
                                ) as CodedChoiceField;

                                if (codedChoiceField?.values) {
                                    const matchingChoice = codedChoiceField.values.find(
                                        (value: CodedChoiceValue) => value.code === override.choiceValue
                                    );

                                    if (matchingChoice) {
                                        (override as SymbologyOverrideChoice).choiceDisplayValue = matchingChoice.description;
                                    }
                                }
                            });
                        }
                    }).catch(() => {
                        // If template loading fails, continue without descriptions
                    });
                }

                const OtherStyleOverride: SymbologyOverrideChoice = {
                    id: 'all-others-' + layer.id,
                    choiceField: this.translate.instant(
                        'TCS.Mapviewer.LayerSymbology.AttributeBasedSymbology.AllOthers'
                    ),
                    choiceValue: this.translate.instant(
                        'TCS.Mapviewer.LayerSymbology.AttributeBasedSymbology.AllOthers'
                    ),
                    lineColor: layer.style.lineColor,
                    fillColor: layer.style.symbol?.fillColor,
                    outlineColor: layer.style.symbol?.outlineColor,
                    color: layer.style.symbol?.color
                };
                let styleOverrides = [...styleOverridesWithId, OtherStyleOverride] as SymbologyOverrideChoice[];
                this.layerSymbologyOverridesMap.set(layer.id, styleOverrides);
                // Initialize sublayersVisibleMap based on initial layer visibility.
                styleOverrides.forEach(sl => {
                    this.sublayersVisibleMap.set(sl.id, layer.visible);
                });
            } else {
                this.layerSymbologyOverridesMap.delete(layer.id);
            }

            this.layerSublayersDropVisibleMap.set(layer.id, false);
        });
    }

    /**
     * Sync missing IDs from style.styleOverrides to styleOverrides array based on filter string.
     * This is needed when a layer is edited and styleOverrides are not updated with id.
     */
    private syncStyleOverrideIds(layer: Layer): SymbologyOverrideChoice[] {
        return (layer.style.styleOverrides as SymbologyOverrideChoice[]).map(override => {
            if (!override.id && override.choiceField && override.choiceValue) {
                const filterString = `$${override.choiceField} EQ '${override.choiceValue}'`;
                const match = (layer.styleOverrides || []).find((so: any) => so.filter === filterString);
                if (match && match.id) {
                    return { ...override, id: match.id };
                }
            }
            return override;
        });
    }

    public toggleSublayersDrop(layerId: string): void {
        this.layerSublayersDropVisibleMap.get(layerId)
            ? this.layerSublayersDropVisibleMap.set(layerId, false)
            : this.layerSublayersDropVisibleMap.set(layerId, true);
    }

    public getLayerOutlineColor(layer: Layer): string {
        return LayerStyleGenerator.getOutlineColorFromStyle(layer.style, layer.geometryType, layer.color);
    }

    public getSublayerOutlineColor(layer: Layer, sublayer: SymbologyOverrideChoice): string {
        return LayerStyleGenerator.getOutlineColorFromOverride(sublayer, layer.geometryType, layer.color);
    }

    public getSublayerFillColor(layer: Layer, sublayer: SymbologyOverrideChoice): string {
        return LayerStyleGenerator.getFillColorFromOverride(sublayer, layer.style, layer.geometryType, layer.color);
    }

    public toggleSublayer(layer: Layer, sublayer: SymbologyOverrideChoice): void {
        // 1. Retrieve the current hidden features map and the set of hidden feature IDs for this layer
        const hiddenSublayerFeaturesMap = this.layersStreams.hideSublayerFeatureIdsStream.getValue();
        const idx = hiddenSublayerFeaturesMap.findIndex(h => h.hasOwnProperty(layer.id));
        let hiddenSet = new Set(idx !== -1 ? hiddenSublayerFeaturesMap[idx][layer.id] : []);

        // 2. Determine the new visibility state for the sublayer and update the map
        const isSublayerVisible = !this.sublayersVisibleMap.get(sublayer.id);
        this.sublayersVisibleMap.set(sublayer.id, isSublayerVisible);

        // 3. Special Case: If the main layer is hidden and a sublayer is being enabled, hide all other sublayers
        if (!layer.visible && isSublayerVisible) {
            this.hideAllOtherSublayersExcept(layer, sublayer, hiddenSet);
            this.updateHiddenFeaturesMap(hiddenSublayerFeaturesMap, idx, layer.id, hiddenSet);
            this.layersStreams.hideSublayerFeatureIdsStream.next(hiddenSublayerFeaturesMap);
            this.syncLayerVisibilityWithSublayers(layer, true); // true because a sublayer is now visible
            return;
        }

        // 4. Get all feature IDs for this sublayer (handle 'All Others' case)
        const sublayerFeatureIds = new Set(
            sublayer.choiceValue ===
            this.translate.instant('TCS.Mapviewer.LayerSymbology.AttributeBasedSymbology.AllOthers')
                ? this.cachedFeatureService
                      .getFeaturesNotInAnyOverride(
                          layer.id,
                          (layer.style.styleOverrides || []) as SymbologyOverrideChoice[]
                      )
                      .map(f => f.id)
                : this.cachedFeatureService.getFeaturesByOverride(layer.id, sublayer).map(f => f.id)
        );

        // 5. Update the hidden set: remove feature IDs if showing, add if hiding
        if (isSublayerVisible) {
            sublayerFeatureIds.forEach(id => hiddenSet.delete(id));
        } else {
            sublayerFeatureIds.forEach(id => hiddenSet.add(id));
        }

        // 6. If no sublayer is visible, clear the hidden set
        const overrideIds = this.layerSymbologyOverridesMap.get(layer.id)?.map(override => override.id) || [];
        const anyVisibleSubLayer = overrideIds.some(id => this.sublayersVisibleMap.get(id));
        if (!anyVisibleSubLayer) {
            hiddenSet.clear();
        }

        // 7. Update the hidden features map and notify subscribers
        this.updateHiddenFeaturesMap(hiddenSublayerFeaturesMap, idx, layer.id, hiddenSet);
        this.layersStreams.hideSublayerFeatureIdsStream.next(hiddenSublayerFeaturesMap);

        // 8. Synchronize the parent layer's visibility with its sublayers
        this.syncLayerVisibilityWithSublayers(layer, anyVisibleSubLayer);
    }

    /**
     * Helper to update the hidden features map for a layer.
     */
    private updateHiddenFeaturesMap(
        hiddenSublayerFeaturesMap: any[],
        idx: number,
        layerId: string,
        hiddenSet: Set<string | number>
    ): void {
        if (hiddenSet.size > 0) {
            if (idx !== -1) {
                hiddenSublayerFeaturesMap[idx][layerId] = Array.from(hiddenSet);
            } else {
                hiddenSublayerFeaturesMap.push({ [layerId]: Array.from(hiddenSet) });
            }
        } else if (idx !== -1) {
            hiddenSublayerFeaturesMap.splice(idx, 1);
        }
    }

    /**
     * Hides all sublayer features except the selected sublayer for a hidden main layer.
     * Adds all feature IDs from other sublayers to the hiddenSet.
     */
    private hideAllOtherSublayersExcept(
        layer: Layer,
        sublayer: SymbologyOverrideChoice,
        hiddenSet: Set<string | number>
    ): void {
        const allOverrides = this.layerSymbologyOverridesMap.get(layer.id) || [];
        allOverrides.forEach(override => {
            if (override.id !== sublayer.id) {
                let features: CachedFeature[];
                if (
                    override.choiceValue ===
                    this.translate.instant('TCS.Mapviewer.LayerSymbology.AttributeBasedSymbology.AllOthers')
                ) {
                    const styleOverrides = (layer.style.styleOverrides || []) as SymbologyOverrideChoice[];
                    features = this.cachedFeatureService.getFeaturesNotInAnyOverride(layer.id, styleOverrides);
                } else {
                    features = this.cachedFeatureService.getFeaturesByOverride(layer.id, override);
                }
                features.forEach(f => hiddenSet.add(f.id));
            }
        });
    }

    /**
     * Synchronizes the visibility state of a parent layer with its sublayers.
     * If the parent layer's visibility does not match the visibility of its sublayers,
     * it toggles the parent layer's visibility accordingly and emits the appropriate
     * event to the corresponding stream.
     */
    private syncLayerVisibilityWithSublayers(layer: Layer, anyVisibleSubLayer: boolean): void {
        if (layer.visible !== anyVisibleSubLayer) {
            const idx = this.layers.findIndex(l => l.id === layer.id);
            if (idx !== -1) {
                //false to avoid emptying the hiddenSublayerFeaturesMap. Especially for step 3 in toggleSublayer
                this.toggle(idx, false);
                if (this.layers[idx].visible) {
                    this.layersStreams.refreshMapWorkspaceLayersStream.next([layer]);
                } else {
                    this.layersStreams.deferHideLayersStream.next([layer]);
                }
                return;
            }
        }
        this.layersStreams.refreshMapWorkspaceLayersStream.next([layer]);
    }

    private resetSublayerVisibilityForLayer(layer: Layer, isVisible: boolean): void {
        const layerOverrideIds = this.layerSymbologyOverridesMap.get(layer.id)?.map(override => override.id);

        if (layerOverrideIds) {
            layerOverrideIds.forEach(id => {
                this.sublayersVisibleMap.set(id, isVisible);
            });
        }
    }

    private isLayerSublayer(layer: Layer): boolean {
        return layer.id?.startsWith('all-others-') || layer.id?.includes('-sublayer-');
    }
}
